import React, { useEffect, useState } from 'react';
import { Phone, PhoneCall, PhoneIncoming } from 'lucide-react';

/**
 * @param {{open?: boolean, isInCall?: boolean, hasIncoming?: boolean, voiceReady?: boolean}} s
 * @returns {{label: string, tone: 'incoming'|'call'|'open'|'idle'}} What the launcher should say and how loudly.
 */
export function launcherState(s = {}) {
  if (s.hasIncoming && !s.isInCall) return { label: 'Incoming call', tone: 'incoming' };
  if (s.isInCall) return { label: 'On call', tone: 'call' };
  if (s.open) return { label: 'Close phone', tone: 'open' };
  return { label: 'Open phone', tone: 'idle' };
}

const TONES = {
  incoming: 'bg-emerald-500 text-white shadow-[0_0_24px_rgba(16,185,129,0.65)] animate-bounce',
  call: 'bg-emerald-500 text-white shadow-[0_0_18px_rgba(16,185,129,0.5)]',
  open: 'bg-civix-panelLight text-civix-purpleSoft ring-1 ring-civix-purple/60',
  idle: 'bg-civix-gradient text-white shadow-glow-purple hover:scale-105',
};

/**
 * Prominent softphone launcher for the global header: gradient pill with a
 * soft "ping" halo while idle, a live-ready dot, and loud states for ringing /
 * in-call. Talks to PhonePanel through the existing civix:phone:* events.
 * @returns {JSX.Element}
 */
export default function PhoneLauncher() {
  const [s, setS] = useState({ open: false, isInCall: false, hasIncoming: false, voiceReady: false });

  useEffect(() => {
    const onState = (e) => setS(e.detail || {});
    window.addEventListener('civix:phone:state', onState);
    return () => window.removeEventListener('civix:phone:state', onState);
  }, []);

  const { label, tone } = launcherState(s);
  const Icon = tone === 'incoming' ? PhoneIncoming : tone === 'call' ? PhoneCall : Phone;

  return (
    <button
      id="phone-panel-btn"
      type="button"
      data-testid="phone-launcher"
      data-tone={tone}
      onClick={() => window.dispatchEvent(new Event('civix:phone:toggle'))}
      title={label}
      aria-label={label}
      className={`relative shrink-0 inline-flex items-center gap-2 h-10 pl-3 pr-3.5 rounded-full text-sm font-bold tracking-wide transition-all duration-200 ${TONES[tone]}`}
    >
      {tone === 'idle' && (
        <span aria-hidden className="absolute inset-0 rounded-full bg-civix-purple/50 animate-ping opacity-60 pointer-events-none" />
      )}
      <Icon className={`relative w-[18px] h-[18px] ${tone === 'idle' ? 'animate-pulse' : ''}`} />
      <span className="relative hidden md:inline">{tone === 'idle' ? 'Phone' : label}</span>
      {s.voiceReady && tone === 'idle' && (
        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-civix-bg" title="Softphone connected & ready" />
      )}
      {tone === 'incoming' && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 ring-2 ring-civix-bg" />}
    </button>
  );
}
